<script lang="ts">
import { countCharacters } from '@/lib/countCharacters'
import { computed, defineComponent, PropType } from 'vue'

/*
  There are two ways to measure a string and neither one is right for every caller, so the
  caller has to say which it means.

  'codeUnits' is String.length. It is what a caller that enforces its own limit with
  String.length, String.slice or the browser's maxlength attribute must be shown, because a
  count measured any other way would disagree with the field the user is typing into.

  'codePoints' counts characters as a person counts them, one emoji being one character. It
  is what a caller that clamps in code points must ask for, for the same reason.

  The default is the code-unit basis this component has always used, so a caller written
  before this prop existed keeps the number it displayed before. Which basis is correct is
  therefore a question about the caller, not about this component, and it is answered where
  the enforcement lives.
*/
export type CountBasis = 'codeUnits' | 'codePoints'

export default defineComponent({
  props: {
    characterLimit: {
      required: true,
      type: Number
    },
    text: {
      type: String,
      default: ''
    },
    countBasis: {
      type: String as PropType<CountBasis>,
      default: 'codeUnits'
    }
  },
  setup(props) {
    const charactersUsed = computed(() =>
      props.countBasis === 'codePoints' ? countCharacters(props.text) : props.text.length
    )
    const charactersRemaining = computed(() => props.characterLimit - charactersUsed.value)
    const isLimitCrossed = computed(() => charactersRemaining.value < 0)

    return { charactersRemaining, isLimitCrossed }
  }
})
</script>
<template>
  <span class="character-count" :class="[isLimitCrossed ? 'limit-crossed' : '']">{{
    charactersRemaining
  }}</span>
</template>
<style scoped>
.character-count {
  font-weight: normal;
  color: #666666;
}

.limit-crossed {
  color: #c93b41;
}
</style>
